import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { SmartSortProduct } from '@iheartjane/dm-sdk';
import { getApplicableGoldTagLabel, useNavigateBack } from '@iheartjane/menu';
import {
  Box,
  Button,
  Flex,
  Loading,
  SearchField,
  Typography,
} from '@iheartjane/reefer';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLocation, useNavigate } from 'react-router';

import { useRecommendedSort } from '@jane/dm/internal';
import {
  InfiniteScrollThreshold,
  Modal,
  ModalHeader,
  NoResults,
  ProductRow,
  SearchInputContainer,
} from '@jane/kiosk/components';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import { searchOptions } from '@jane/kiosk/data-access';
import {
  useHideVirtualKeyboard,
  useKioskSpacing,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import {
  useBrandSpecials,
  useProductSort,
  useSearchContext,
} from '@jane/kiosk/providers';
import { trackSearchedTheMenu } from '@jane/kiosk/util';
import { buildSearchFilter } from '@jane/search/util';
import { useGetStore } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';

export const Search = () => {
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { storeId, store: initialStore } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const { defaultSpacing, isIpad } = useKioskSpacing();
  const { searchState, resetSearchState, setSearchState } = useSearchContext();
  const [searchTerm, setSearchTerm] = useState(searchState.searchText ?? '');
  const { inView, ref } = useInView();
  const { optionalFiltersString, searchSort } = useProductSort({
    defaultSortName: 'Relevance',
    optionalFilters: store.optional_filters,
    store,
  });
  const { brandSpecials } = useBrandSpecials();

  const closeModal = useCallback(() => {
    goBack();
    setSearchState({ searchText: undefined });
  }, [goBack, setSearchState]);

  const options = searchOptions({
    optionalFilters: optionalFiltersString,
    storeId,
  });

  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters: options.staticFilters,
  });

  const {
    numHits: totalHits,
    products,
    hasNextPage,
    fetchNextPage,
    isSuccess,
    isLoading,
    instance,
  } = useRecommendedSort<MaterializedMenuProduct>({
    disableAds: true,
    enabled: !!searchTerm,
    numColumns: 1,
    searchAttributes: ['*'],
    searchFilter,
    searchQuery: searchTerm,
    searchSort,
    storeId,
  });

  useEffect(() => {
    if (inView && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, fetchNextPage, hasNextPage]);

  const viewProduct = useCallback(
    (product: SmartSortProduct<MaterializedMenuProduct>) => {
      product.click();
      trackSearchedTheMenu({
        product: product.attributes,
        searchTerm,
        selectedAutocomplete: true,
        store,
        totalCountInMenu: Number(store.product_count),
        totalCountInSearch: totalHits,
      });
      setSearchState({ searchText: searchTerm });
      return navigate(
        `${pathname}/products/${product.attributes.product_id}?pdpDepth=2`
      );
    },
    [navigate, pathname, instance, searchTerm, setSearchState, store, totalHits]
  );

  const navigateToSearchResults = useCallback(
    (searchTerm = '') => {
      trackSearchedTheMenu({
        searchTerm,
        selectedAutocomplete: false,
        store,
        totalCountInMenu: Number(store.product_count),
        totalCountInSearch: totalHits,
      });
      resetSearchState();
      setSearchState({ searchText: searchTerm });
      navigate(
        `/stores/${storeId}/menu/search-results?searchText=${searchTerm}`
      );
    },
    [navigate, resetSearchState, setSearchState, store, storeId, totalHits]
  );

  const renderProductRow = useCallback(
    (product: SmartSortProduct<MaterializedMenuProduct>) => {
      const janeGoldTagLabel = getApplicableGoldTagLabel({
        brandSpecials,
        applicableBrandSpecialIds:
          product.attributes.applicable_brand_special_ids,
      });
      return (
        <ProductRow
          key={product.objectId}
          janeGoldTagLabel={janeGoldTagLabel}
          onClick={viewProduct}
          product={product.attributes}
          searchTerm={searchTerm}
          showCategory
          productInstance={product}
        />
      );
    },
    [searchTerm, viewProduct]
  );

  useHideVirtualKeyboard();

  const showProductResults = isSuccess && products.length > 0;

  const showEmptyResults = isSuccess && products.length <= 0;

  const isQueryLoading = isLoading && !isSuccess;

  return (
    <Modal
      appId="root"
      contentLabel="Product Search"
      onRequestClose={closeModal}
      open
      overflow="scroll"
      overlayClose
      variant="standard-dialogue"
    >
      <ModalHeader onRequestClose={closeModal}>
        <Flex
          alignItems="center"
          grow={1}
          justifyContent="space-between"
          alignSelf="auto"
        >
          <Typography mr={96} variant={isIpad ? 'h3-semibold' : 'h2-semibold'}>
            Search
          </Typography>
          <SearchInputContainer>
            <SearchField
              ref={searchInputRef}
              autoFocus
              autocomplete="cc-name"
              debounceDelay={300}
              defaultValue={searchTerm}
              enterKeyHint="search"
              hideSearchIcon
              isDebounced
              label="Search products"
              labelHidden
              name="product-search"
              onChange={setSearchTerm}
              onEnterPress={() => {
                navigateToSearchResults(searchInputRef.current?.value);
              }}
            />
          </SearchInputContainer>
          <Button
            ariaLabel="view detailed results"
            disabled={!searchTerm}
            ml={16}
            label="Search"
            onClick={() => {
              navigateToSearchResults(searchTerm);
            }}
            size={isIpad ? 'small' : 'default'}
          />
        </Flex>
      </ModalHeader>
      {showProductResults && (
        <Modal.Content padding={false}>
          <Box
            p={defaultSpacing}
            height="100%"
            width="100%"
            position="relative"
          >
            <Flex flexDirection="column" height="100%">
              <>
                <Typography mb={32} variant="h3-semibold">
                  Product results
                </Typography>
                {products.map(renderProductRow)}
              </>
            </Flex>
            <InfiniteScrollThreshold ref={ref} />
          </Box>
        </Modal.Content>
      )}

      {isQueryLoading && (
        <Flex alignItems="center" height="100%" justifyContent="center">
          <Loading color="black" size="lg" />
        </Flex>
      )}
      {showEmptyResults && <NoResults />}
    </Modal>
  );
};
